import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { retryUnlessNotFound } from "@/lib/api/client";
import {
  getTemplates,
  getTemplate,
  createWidgetTemplate,
  updateWidgetTemplate,
} from "@/lib/api/templates";
import type {
  CreateWidgetTemplateRequest,
  UpdateWidgetTemplateRequest,
} from "@/types/widget";

export const useTemplatesQuery = () =>
  useQuery({
    queryKey: ["templates"],
    queryFn: getTemplates,
  });

export const useTemplateQuery = (id: string) =>
  useQuery({
    queryKey: ["template", id],
    queryFn: () => getTemplate(id).then((res) => res.data),
    enabled: !!id,
    retry: retryUnlessNotFound,
  });

export const useCreateTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWidgetTemplateRequest) =>
      createWidgetTemplate(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
};

export const useUpdateTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateWidgetTemplateRequest;
    }) => updateWidgetTemplate(id, data).then((res) => res.data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      queryClient.invalidateQueries({ queryKey: ["template", variables.id] });
    },
  });
};
